import './ZubereitungView.css';
import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Modal, ListGroup, Form, Pagination } from 'react-bootstrap';
import { FormControlLabel, Switch } from '@mui/material';
import axios from 'axios';
import { API_URL } from '../config';
import { useSound } from '../hooks/useSound';

function ZubereitungView() {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const { playNotification } = useSound();
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved ? JSON.parse(saved) : true;
  });
  const [drinks, setDrinks] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showOnlyOpen, setShowOnlyOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ordersPerPage = 10;

  useEffect(() => {
    fetchDrinks();
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  const fetchDrinks = async () => {
    try {
      const response = await axios.get(`${API_URL}/drinks`);
      const drinksMap = {};
      response.data.forEach(drink => {
        drinksMap[drink.id] = drink;
      });
      setDrinks(drinksMap);
    } catch (error) {
      console.error('Error fetching drinks:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`);
      const newOrders = response.data;
      
      // Spiele Sound wenn neue Bestellung eingegangen ist
      if (soundEnabled && orders.length < newOrders.length) {
        playNotification();
      }
      
      const sortedOrders = newOrders.sort((a, b) => {
        if (a.status === b.status) {
          return b.id - a.id;
        }
        return a.status === 'offen' ? -1 : 1;
      });
      setOrders(sortedOrders);
      setError(null);
    } catch (error) {
      setError('Fehler beim Laden der Bestellungen');
      console.error('Error fetching orders:', error);
    }
  };

  const markAsCompleted = async (orderId, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API_URL}/orders/${orderId}/complete`);
      fetchOrders();
    } catch (error) {
      console.error('Error marking order as completed:', error);
    }
  };

  const getDrinkName = (drinkId) => {
    return drinks[drinkId]?.name || `Getränk ${drinkId}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'offen':
        return <Badge bg="warning">Offen</Badge>;
      case 'in_zubereitung':
        return <Badge bg="info">Fertig</Badge>;
      case 'fertig':
        return <Badge bg="success">Ausgeliefert</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  // Filter orders based on showOnlyOpen
  const filteredOrders = showOnlyOpen 
    ? orders.filter(order => order.status === 'offen')
    : orders;

  // Calculate pagination
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  // Generate pagination items
  let paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item 
        key={number} 
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  return (
    <div className="container mt-4 pb-4">
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <h2>Zubereitung</h2>
        <FormControlLabel
          control={
            <Switch
              checked={soundEnabled}
              onChange={(e) => setSoundEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="Sound-Benachrichtigungen"
        />
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2></h2>
        <Form.Check
          type="switch"
          id="show-open-orders"
          label="Nur offene Bestellungen"
          checked={showOnlyOpen}
          onChange={(e) => setShowOnlyOpen(e.target.checked)}
        />
      </div>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th className="col-bestellnr">Nr.</th>
              <th className="col-kunde">Kunde</th>
              <th className="col-getraenke">Getränke</th>
              <th className="col-status">Status</th>
              <th className="col-aktionen">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {currentOrders.map((order) => (
              <tr 
                key={order.id} 
                className={order.status === 'offen' ? 'table-warning' : order.status === 'fertig' ? 'table-success' : ''}
                onClick={() => handleOrderClick(order)}
                style={{ cursor: 'pointer' }}
              >
                <td data-label="Nr.">{order.id}</td>
                <td data-label="Kunde">{order.customer_name}</td>
                <td data-label="Getränke">
                  {order.items.map((item, index) => (
                    <div key={index} className="mb-1">
                      {getDrinkName(item.drink_id)} ({item.quantity}x)
                    </div>
                  ))}
                </td>
                <td data-label="Status" className="text-center">
                  {getStatusBadge(order.status)}
                </td>
                <td data-label="Aktionen" onClick={(e) => e.stopPropagation()} className="text-center">
                  {order.status === 'offen' && (
                    <Button variant="primary" size="sm" className="w-100" onClick={(e) => markAsCompleted(order.id, e)}>
                      Als fertig markieren
                    </Button>
                  )}
                  {order.status === 'in_zubereitung' && (
                    <Button variant="success" size="sm" className="w-100" onClick={(e) => markAsCompleted(order.id, e)}>
                      Als ausgeliefert markieren
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(curr => Math.max(1, curr - 1))} disabled={currentPage === 1} />
            {paginationItems}
            <Pagination.Next onClick={() => setCurrentPage(curr => Math.min(totalPages, curr + 1))} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Bestellung #{selectedOrder?.id} - {selectedOrder?.customer_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="d-flex align-items-center gap-2">
            Status: {selectedOrder && getStatusBadge(selectedOrder.status)}
          </h5>
          <h5 className="mt-4">Bestellte Getränke:</h5>
          <ListGroup>
            {selectedOrder?.items.map((item, index) => (
              <ListGroup.Item 
                key={index}
                className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2"
              >
                <div>
                  <h6 className="mb-0">{getDrinkName(item.drink_id)}</h6>
                  <small className="text-muted">
                    Einzelpreis: {drinks[item.drink_id]?.price.toFixed(2)} M$
                  </small>
                </div>
                <div className="text-sm-end">
                  <h5 className="mb-0">
                    <Badge bg="primary" pill>
                      {item.quantity}x
                    </Badge>
                  </h5>
                  <small className="text-muted">
                    {(drinks[item.drink_id]?.price * item.quantity).toFixed(2)} M$
                  </small>
                </div>
              </ListGroup.Item>
            ))}
            <ListGroup.Item className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 bg-light">
              <h6 className="mb-0">Gesamtbetrag</h6>
              <h5 className="mb-0">{selectedOrder?.total_price.toFixed(2)} M$</h5>
            </ListGroup.Item>
          </ListGroup>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-grid gap-2 d-sm-flex justify-content-sm-end w-100">
            {selectedOrder?.status === 'offen' && (
              <Button variant="primary" onClick={(e) => markAsCompleted(selectedOrder.id, e)}>
                Als fertig markieren
              </Button>
            )}
            {selectedOrder?.status === 'in_zubereitung' && (
              <Button variant="success" onClick={(e) => markAsCompleted(selectedOrder.id, e)}>
                Als ausgeliefert markieren
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Schließen
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ZubereitungView;
